import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ShipStatusTracker from './ship-status-tracker';
import { VesselSection, VESSEL_DATA } from './types/vessel';

type DeckType = 'upper' | 'lower' | `middle${number}`;

interface DeckSections {
  upper: VesselSection[];
  lower?: VesselSection[];
  [key: `middle${number}`]: VesselSection[];
}

interface InteractiveShipHullProps {
  upperDeckImage: string;
  bottomDeckImage: string;
  selectedVessel: string;
  deckSections: DeckSections;
  riggingSections: VesselSection[];
  onDeckUpdate: (deckType: DeckType, sections: VesselSection[]) => void;
  onRiggingUpdate: (sections: VesselSection[]) => void;
  onAllDecksUpdate: (deckSections: DeckSections) => void;
  onRepairAll: (deckSections: DeckSections, riggingSections: VesselSection[]) => void;
}

const InteractiveShipHull: React.FC<InteractiveShipHullProps> = ({ 
  upperDeckImage, 
  bottomDeckImage,
  selectedVessel,
  deckSections,
  riggingSections,
  onDeckUpdate,
  onRiggingUpdate,
  onAllDecksUpdate,
  onRepairAll
}) => {
  const [selectedDeck, setSelectedDeck] = useState<number>(0);
  const [selectedSection, setSelectedSection] = useState<{ type: 'hull' | 'rigging', index: number } | null>(null);
  const [hpAdjustment, setHpAdjustment] = useState<number>(0);
  const [flashingSection, setFlashingSection] = useState<{ type: 'hull' | 'rigging', index: number } | null>(null);

  const riggingLabels = ["Foremast", "Main Mast", "Aft Mast"];

  // Reset to top deck when vessel changes
  useEffect(() => {
    setSelectedDeck(0);
  }, [selectedVessel]);

  const getDeckType = (deckIndex: number): DeckType => {
    const vessel = VESSEL_DATA[selectedVessel];
    if (vessel.decks === 2) {
      return deckIndex === 0 ? 'upper' : 'lower';
    }
    if (deckIndex === 0) return 'upper';
    if (deckIndex === vessel.decks - 1) return 'lower';
    return `middle${deckIndex}`;
  };

  const handleSectionClick = (index: number, type: 'hull' | 'rigging') => {
    setSelectedSection({ type, index });
  };

  const handleHPAdjustment = (value: number) => {
    if (!selectedSection) return;

    if (selectedSection.type === 'rigging') {
      const newSections = [...riggingSections];
      const currentHP = newSections[selectedSection.index];
      newSections[selectedSection.index] = {
        ...currentHP,
        current: Math.max(0, Math.min(currentHP.max, currentHP.current + value))
      };
      onRiggingUpdate(newSections);
    } else {
      // Hull section
      const deckType = getDeckType(selectedDeck);
      const currentDeckSections = deckSections[deckType] || [];
      const newSections = [...currentDeckSections];
      const currentHP = newSections[selectedSection.index];
      newSections[selectedSection.index] = {
        ...currentHP,
        current: Math.max(0, Math.min(currentHP.max, currentHP.current + value))
      };
      onDeckUpdate(deckType, newSections);
    }
  };

  const handleRandomDamage = () => {
    const vessel = VESSEL_DATA[selectedVessel];
    const allSections = [
      ...deckSections.upper,
      ...Object.entries(deckSections)
        .filter(([key]) => key.startsWith('middle'))
        .flatMap(([_, sections]) => sections as VesselSection[]),
      ...(deckSections.lower || []),
      ...(riggingSections || [])
    ];
    const randomIndex = Math.floor(Math.random() * allSections.length);

    if (randomIndex < deckSections.upper.length) {
      // Upper deck section
      const newSections = [...deckSections.upper];
      newSections[randomIndex] = {
        ...newSections[randomIndex],
        current: Math.max(0, newSections[randomIndex].current - hpAdjustment)
      };
      onDeckUpdate('upper', newSections);
      setSelectedDeck(0);
      setFlashingSection({ type: 'hull', index: randomIndex });
      setSelectedSection({ type: 'hull', index: randomIndex });
    } else if (randomIndex < deckSections.upper.length + Object.entries(deckSections)
      .filter(([key]) => key.startsWith('middle'))
      .reduce((sum, [_, sections]) => sum + (sections as VesselSection[]).length, 0)) {
      // Middle deck section
      let remainingIndex = randomIndex - deckSections.upper.length;
      for (let i = 1; i < vessel.decks - 1; i++) {
        const middleDeckKey = `middle${i}` as DeckType;
        const middleDeckSections = deckSections[middleDeckKey] || [];
        if (remainingIndex < middleDeckSections.length) {
          const newSections = [...middleDeckSections];
          newSections[remainingIndex] = {
            ...newSections[remainingIndex],
            current: Math.max(0, newSections[remainingIndex].current - hpAdjustment)
          };
          onDeckUpdate(middleDeckKey, newSections);
          setSelectedDeck(i);
          setFlashingSection({ type: 'hull', index: remainingIndex });
          setSelectedSection({ type: 'hull', index: remainingIndex });
          break;
        }
        remainingIndex -= middleDeckSections.length;
      }
    } else if (deckSections.lower) {
      // Lower deck section
      const lowerIndex = randomIndex - deckSections.upper.length - 
        Object.entries(deckSections)
          .filter(([key]) => key.startsWith('middle'))
          .reduce((sum, [_, sections]) => sum + (sections as VesselSection[]).length, 0);
      
      // Ensure the index is within bounds
      if (lowerIndex >= 0 && lowerIndex < deckSections.lower.length) {
        const newSections = [...deckSections.lower];
        newSections[lowerIndex] = {
          ...newSections[lowerIndex],
          current: Math.max(0, newSections[lowerIndex].current - hpAdjustment)
        };
        onDeckUpdate('lower', newSections);
        setSelectedDeck(vessel.decks - 1);
        setFlashingSection({ type: 'hull', index: lowerIndex });
        setSelectedSection({ type: 'hull', index: lowerIndex });
      } else {
        // If the index is out of bounds, try dealing damage to rigging instead
        if (riggingSections && riggingSections.length > 0) {
          const riggingIndex = Math.floor(Math.random() * riggingSections.length);
          const newSections = [...riggingSections];
          newSections[riggingIndex] = {
            ...newSections[riggingIndex],
            current: Math.max(0, newSections[riggingIndex].current - hpAdjustment)
          };
          onRiggingUpdate(newSections);
          setFlashingSection({ type: 'rigging', index: riggingIndex });
          setSelectedSection({ type: 'rigging', index: riggingIndex });
        }
      }
    } else if (riggingSections && riggingSections.length > 0) {
      // Rigging section
      const middleDeckSectionsLength = Object.entries(deckSections)
        .filter(([key]) => key.startsWith('middle'))
        .reduce((sum, [_, sections]) => sum + (sections as VesselSection[]).length, 0);
      const lowerDeckLength = (deckSections.lower as VesselSection[] | undefined)?.length ?? 0;
      const riggingIndex = randomIndex - deckSections.upper.length - middleDeckSectionsLength - lowerDeckLength;
      
      // Ensure the index is within bounds
      if (riggingIndex >= 0 && riggingIndex < riggingSections.length) {
        const newSections = [...riggingSections];
        newSections[riggingIndex] = {
          ...newSections[riggingIndex],
          current: Math.max(0, newSections[riggingIndex].current - hpAdjustment)
        };
        onRiggingUpdate(newSections);
        setFlashingSection({ type: 'rigging', index: riggingIndex });
        setSelectedSection({ type: 'rigging', index: riggingIndex });
      }
    }

    // Clear flashing after a delay
    setTimeout(() => setFlashingSection(null), 1000);
  };

  const getColor = (hp: VesselSection, isFlashing: boolean): string => {
    if (isFlashing) return 'rgba(255, 0, 0, 0.5)'; // Flashing red for damage
    if (hp.current <= 0) return 'rgba(128, 0, 0, 0.5)'; // Destroyed
    if (hp.current <= hp.max / 2) return 'rgba(255, 255, 0, 0.5)'; // Damaged
    return 'rgba(0, 255, 0, 0)'; // Normal
  };

  const handleDealHoledDamage = () => {
    const vessel = VESSEL_DATA[selectedVessel];
    const sectionsPerDeck = vessel.sectionsPerDeck;
    const halfHP = vessel.sectionHP / 2;

    // Create a new deckSections object to update all affected decks
    const newDeckSections = { ...deckSections };

    // Track which sections are being affected for visual feedback
    const affectedSections: Array<{ type: 'hull', index: number, deck: number }> = [];

    // Find all sections at 0 HP across all decks
    const zeroHPSections: Array<{ deckType: DeckType, deckIndex: number, sectionIndex: number }> = [];

    // Check upper deck
    const upperDeckSections = deckSections.upper || [];
    upperDeckSections.forEach((section, index) => {
      if (section.current === 0 && !section.hasDealtHoleDamage) {
        zeroHPSections.push({ deckType: 'upper', deckIndex: 0, sectionIndex: index });
      }
    });

    // Check middle decks
    Object.entries(deckSections)
      .filter(([key]) => key.startsWith('middle'))
      .forEach(([key, sections]) => {
        const deckIndex = parseInt(key.replace('middle', ''));
        (sections as VesselSection[]).forEach((section, index) => {
          if (section.current === 0 && !section.hasDealtHoleDamage) {
            zeroHPSections.push({ deckType: key as DeckType, deckIndex, sectionIndex: index });
          }
        });
      });

    // Check lower deck
    if (deckSections.lower) {
      deckSections.lower.forEach((section, index) => {
        if (section.current === 0 && !section.hasDealtHoleDamage) {
          zeroHPSections.push({ deckType: 'lower', deckIndex: vessel.decks - 1, sectionIndex: index });
        }
      });
    }

    // If no sections are at 0 HP, do nothing
    if (zeroHPSections.length === 0) {
      return;
    }

    // Process each 0 HP section
    zeroHPSections.forEach(({ deckType, deckIndex, sectionIndex }) => {
      const currentDeckSections = newDeckSections[deckType] || [];
      
      // Deal damage to adjacent sections on the same deck
      const sectionsPerRow = 2;
      const currentRow = Math.floor(sectionIndex / sectionsPerRow);
      const currentCol = sectionIndex % sectionsPerRow;
      
      // Left adjacent section (same row, previous column)
      if (currentCol > 0) {
        const leftIndex = sectionIndex - 1;
        currentDeckSections[leftIndex] = {
          ...currentDeckSections[leftIndex],
          current: Math.max(0, currentDeckSections[leftIndex].current - halfHP)
        };
        affectedSections.push({ type: 'hull', index: leftIndex, deck: deckIndex });
      }
      
      // Right adjacent section (same row, next column)
      if (currentCol < sectionsPerRow - 1) {
        const rightIndex = sectionIndex + 1;
        currentDeckSections[rightIndex] = {
          ...currentDeckSections[rightIndex],
          current: Math.max(0, currentDeckSections[rightIndex].current - halfHP)
        };
        affectedSections.push({ type: 'hull', index: rightIndex, deck: deckIndex });
      }
      
      // Top adjacent section (previous row, same column)
      if (currentRow > 0) {
        const topIndex = sectionIndex - sectionsPerRow;
        currentDeckSections[topIndex] = {
          ...currentDeckSections[topIndex],
          current: Math.max(0, currentDeckSections[topIndex].current - halfHP)
        };
        affectedSections.push({ type: 'hull', index: topIndex, deck: deckIndex });
      }
      
      // Bottom adjacent section (next row, same column)
      const totalRows = Math.ceil(sectionsPerDeck / sectionsPerRow);
      if (currentRow < totalRows - 1) {
        const bottomIndex = sectionIndex + sectionsPerRow;
        if (bottomIndex < sectionsPerDeck) {
          currentDeckSections[bottomIndex] = {
            ...currentDeckSections[bottomIndex],
            current: Math.max(0, currentDeckSections[bottomIndex].current - halfHP)
          };
          affectedSections.push({ type: 'hull', index: bottomIndex, deck: deckIndex });
        }
      }

      // Deal damage to the section directly above (on the deck above)
      if (deckIndex > 0) {
        const upperDeckType = getDeckType(deckIndex - 1);
        const upperDeckSections = newDeckSections[upperDeckType] || [];
        if (upperDeckSections.length > sectionIndex) {
          const newUpperDeckSections = [...upperDeckSections];
          newUpperDeckSections[sectionIndex] = {
            ...newUpperDeckSections[sectionIndex],
            current: Math.max(0, newUpperDeckSections[sectionIndex].current - halfHP)
          };
          newDeckSections[upperDeckType] = newUpperDeckSections;
          affectedSections.push({ type: 'hull', index: sectionIndex, deck: deckIndex - 1 });
        }
      }

      // Deal damage to the section directly below (on the deck below)
      if (deckIndex < vessel.decks - 1) {
        const lowerDeckType = getDeckType(deckIndex + 1);
        const lowerDeckSections = newDeckSections[lowerDeckType] || [];
        if (lowerDeckSections.length > sectionIndex) {
          const newLowerDeckSections = [...lowerDeckSections];
          newLowerDeckSections[sectionIndex] = {
            ...newLowerDeckSections[sectionIndex],
            current: Math.max(0, newLowerDeckSections[sectionIndex].current - halfHP)
          };
          newDeckSections[lowerDeckType] = newLowerDeckSections;
          affectedSections.push({ type: 'hull', index: sectionIndex, deck: deckIndex + 1 });
        }
      }

      // Mark the section as having dealt hole damage
      currentDeckSections[sectionIndex] = {
        ...currentDeckSections[sectionIndex],
        hasDealtHoleDamage: true
      };

      // Update the deck sections
      newDeckSections[deckType] = currentDeckSections;
    });

    // Update all decks at once
    onAllDecksUpdate(newDeckSections);

    // Flash all affected sections
    affectedSections.forEach(({ type, index, deck }) => {
      if (deck === selectedDeck) {
        setFlashingSection({ type, index });
      }
    });

    // Clear flashing after a delay
    setTimeout(() => setFlashingSection(null), 1000);
  };

  // Check if any hull sections are at 0 HP and haven't dealt hole damage
  const hasZeroHPSections = () => {
    // Check upper deck
    const upperDeckSections = deckSections.upper || [];
    if (upperDeckSections.some(section => section.current === 0 && !section.hasDealtHoleDamage)) {
      return true;
    }

    // Check middle decks
    const middleDecksHaveZeroHP = Object.entries(deckSections)
      .filter(([key]) => key.startsWith('middle'))
      .some(([_, sections]) => 
        (sections as VesselSection[]).some(section => section.current === 0 && !section.hasDealtHoleDamage)
      );
    if (middleDecksHaveZeroHP) {
      return true;
    }

    // Check lower deck
    if (deckSections.lower) {
      return deckSections.lower.some(section => section.current === 0 && !section.hasDealtHoleDamage);
    }

    return false;
  };

  const handleRepairAll = () => {
    // Repair all deck sections
    const repairDeck = (sections: VesselSection[]) => {
      return sections.map(section => ({
        ...section,
        current: section.max,
        hasDealtHoleDamage: false
      }));
    };

    // Create a new deckSections object with all decks repaired
    const repairedDeckSections: DeckSections = {
      upper: repairDeck(deckSections.upper.map(section => ({ ...section }))),
      ...(deckSections.lower && { 
        lower: repairDeck(deckSections.lower.map(section => ({ ...section }))) 
      }),
      ...Object.fromEntries(
        Object.entries(deckSections)
          .filter(([key]) => key.startsWith('middle'))
          .map(([key, sections]) => [
            key, 
            repairDeck((sections as VesselSection[]).map(section => ({ ...section })))
          ])
      )
    };

    // Repair all rigging sections
    const repairedRigging = riggingSections.map(section => ({
      ...section,
      current: section.max
    }));

    // Update decks and rigging together in a single ship update so the two
    // writes don't clobber each other (each rebuilds the ship from stale state).
    onRepairAll(repairedDeckSections, repairedRigging);
  };

  const handleDeckChange = (direction: 'up' | 'down') => {
    const vessel = VESSEL_DATA[selectedVessel];
    const maxDeck = vessel.decks - 1; // Convert to 0-based index

    if (direction === 'up' && selectedDeck > 0) {
      setSelectedDeck(selectedDeck - 1);
    } else if (direction === 'down' && selectedDeck < maxDeck) {
      setSelectedDeck(selectedDeck + 1);
    }
  };

  const getDeckName = (deckIndex: number): string => {
    const vessel = VESSEL_DATA[selectedVessel];
    if (vessel.decks === 2) {
      return deckIndex === 0 ? 'Upper Deck' : 'Lower Deck';
    }
    if (deckIndex === 0) return 'Upper Deck';
    if (deckIndex === vessel.decks - 1) return 'Lower Deck';
    return `Middle Deck ${deckIndex}`;
  };

  const currentDeckType = getDeckType(selectedDeck);
  const currentDeckSections = deckSections[currentDeckType] || [];
  const currentDeckImage = selectedDeck === 0 ? upperDeckImage : bottomDeckImage;

  // Calculate grid dimensions based on sections per deck
  const sectionsPerRow = 2;
  const rows = Math.ceil(currentDeckSections.length / sectionsPerRow);
  const sectionHeight = `${100 / rows}%`;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold">{getDeckName(selectedDeck)}</h2>
          <div className="flex gap-2">
            {selectedDeck > 0 && (
              <Button onClick={() => handleDeckChange('up')}>
                Go to {getDeckName(selectedDeck - 1)}
              </Button>
            )}
            {selectedDeck < VESSEL_DATA[selectedVessel].decks - 1 && (
              <Button onClick={() => handleDeckChange('down')}>
                Go to {getDeckName(selectedDeck + 1)}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-4">
        {/* Rigging sections */}
        {riggingSections && riggingSections.length > 0 && (
          <div className="w-1/5">
            <h3 className="text-sm font-semibold mb-1">Rigging</h3>
            <div className="space-y-2">
              {riggingSections.map((section, index) => (
                <div
                  key={`rigging-${index}`}
                  className={`p-2 border rounded cursor-pointer transition-colors ${
                    selectedSection?.type === 'rigging' && selectedSection.index === index
                      ? 'border-primary'
                      : ''
                  }`}
                  style={{ backgroundColor: getColor(section, flashingSection?.type === 'rigging' && flashingSection.index === index) }}
                  onClick={() => handleSectionClick(index, 'rigging')}
                >
                  <div className="text-center">
                    <div className="text-sm font-medium">{riggingLabels[index] || `Mast ${index + 1}`}</div>
                    <div className="text-xs">{section.current}/{section.max} HP</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Hull sections */}
        <div className="flex-1 relative">
          <img
            src={currentDeckImage}
            alt={`${getDeckName(selectedDeck)} View`}
            className="w-full h-auto"
          />
          <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${sectionsPerRow}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }}>
            {currentDeckSections.map((section, index) => (
              <div
                key={`hull-${index}`}
                className={`border cursor-pointer transition-colors ${
                  selectedSection?.type === 'hull' && selectedSection.index === index
                    ? 'border-primary'
                    : ''
                }`}
                style={{ backgroundColor: getColor(section, flashingSection?.type === 'hull' && flashingSection.index === index) }}
                onClick={() => handleSectionClick(index, 'hull')}
              >
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="text-sm font-medium">Section {index + 1}</div>
                    <div className="text-xs">{section.current}/{section.max} HP</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="w-64 space-y-4">
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">HP Adjustment</h3>
            <Input
              type="number"
              value={hpAdjustment}
              onChange={(e) => setHpAdjustment(Number(e.target.value))}
              placeholder="Enter HP amount"
            />
            <div className="flex gap-2">
              <Button onClick={() => handleHPAdjustment(hpAdjustment)} className="flex-1">
                Add HP
              </Button>
              <Button onClick={() => handleHPAdjustment(-hpAdjustment)} className="flex-1">
                Remove HP
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Damage Controls</h3>
            <Button onClick={handleRandomDamage} className="w-full">
              Deal Random Damage
            </Button>
            <Button 
              onClick={handleDealHoledDamage}
              disabled={!hasZeroHPSections()}
              className="w-full"
            >
              Deal Hole Damage
            </Button>
            <Button onClick={handleRepairAll} className="w-full">
              Repair All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveShipHull;